import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ImageOffIcon, ZoomOutIcon, XIcon, ZapIcon, LightbulbIcon, ToggleLeftIcon } from 'lucide-react';
// Define Sensor Types
export type SensorType = 'SEO' | 'SEL' | 'SES';
export type ViewMode = 'energy' | 'temperature' | 'comfort';
export interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
  x: number;
  y: number;
}
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: any;
}
interface FloorPlanHeatMapProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  isZoomed?: boolean;
  onZoomOut?: () => void;
  onSelectSensor?: (sensor: Sensor) => void;
  selectedSensorId?: string | null;
  viewMode?: ViewMode;
}
// Mock room data for different view modes
const ROOM_ENERGY: Record<string, number> = {
  living: 12.4,
  bedroom: 5.8,
  kitchen: 8.2,
  bathroom: 2.1
};
const ROOM_COMFORT: Record<string, number> = {
  living: 92,
  bedroom: 78,
  kitchen: 65,
  bathroom: 85
};
// Mock Sensor Data
const MOCK_SENSORS: Record<string, Sensor[]> = {
  living: [{
    id: 'seo-living-1',
    type: 'SEO',
    label: 'SEO-01',
    roomId: 'living',
    active: true,
    x: 30,
    y: 40
  }, {
    id: 'sel-living-1',
    type: 'SEL',
    label: 'SEL-01',
    roomId: 'living',
    active: true,
    x: 60,
    y: 25
  }, {
    id: 'ses-living-1',
    type: 'SES',
    label: 'SES-01',
    roomId: 'living',
    active: false,
    x: 85,
    y: 70
  }],
  bedroom: [{
    id: 'seo-bedroom-1',
    type: 'SEO',
    label: 'SEO-02',
    roomId: 'bedroom',
    active: true,
    x: 25,
    y: 50
  }, {
    id: 'sel-bedroom-1',
    type: 'SEL',
    label: 'SEL-02',
    roomId: 'bedroom',
    active: true,
    x: 50,
    y: 30
  }, {
    id: 'ses-bedroom-1',
    type: 'SES',
    label: 'SES-02',
    roomId: 'bedroom',
    active: true,
    x: 80,
    y: 60
  }],
  kitchen: [{
    id: 'seo-kitchen-1',
    type: 'SEO',
    label: 'SEO-03',
    roomId: 'kitchen',
    active: true,
    x: 35,
    y: 45
  }, {
    id: 'sel-kitchen-1',
    type: 'SEL',
    label: 'SEL-03',
    roomId: 'kitchen',
    active: false,
    x: 65,
    y: 30
  }, {
    id: 'ses-kitchen-1',
    type: 'SES',
    label: 'SES-03',
    roomId: 'kitchen',
    active: true,
    x: 20,
    y: 70
  }],
  bathroom: [{
    id: 'seo-bathroom-1',
    type: 'SEO',
    label: 'SEO-04',
    roomId: 'bathroom',
    active: true,
    x: 40,
    y: 40
  }, {
    id: 'sel-bathroom-1',
    type: 'SEL',
    label: 'SEL-04',
    roomId: 'bathroom',
    active: true,
    x: 60,
    y: 60
  }]
};
const ROOM_COORDINATES: Record<string, {
  top: string;
  left: string;
  width: string;
  height: string;
}> = {
  living: {
    top: '48%',
    left: '36%',
    width: '36%',
    height: '46%'
  },
  bedroom: {
    top: '58%',
    left: '5%',
    width: '29.5%',
    height: '24%'
  },
  kitchen: {
    top: '10%',
    left: '41%',
    width: '25%',
    height: '36%'
  },
  bathroom: {
    top: '48%',
    left: '82%',
    width: '15%',
    height: '12%'
  }
};
const DEFAULT_COORDS = {
  top: '40%',
  left: '40%',
  width: '20%',
  height: '20%'
};
const parsePercent = (val: string) => parseFloat(val.replace('%', ''));
const FLOOR_PLAN_URL = `https://iot.seqsys.net/api/images/public/zETox5yNzS8B5amHIuf9RzijqeRUJs9E`;
export function FloorPlanHeatMap({
  rooms,
  selectedRoomId,
  onSelectRoom,
  isZoomed = false,
  onZoomOut,
  onSelectSensor,
  selectedSensorId,
  viewMode = 'temperature'
}: FloorPlanHeatMapProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const getSensorIcon = (type: SensorType) => {
    switch (type) {
      case 'SEO':
        return ZapIcon;
      case 'SEL':
        return LightbulbIcon;
      case 'SES':
        return ToggleLeftIcon;
      default:
        return ZapIcon;
    }
  };
  const getSensorColor = (type: SensorType) => {
    switch (type) {
      case 'SEO':
        return 'text-violet-400 bg-violet-500/20 border-violet-400';
      case 'SEL':
        return 'text-amber-400 bg-amber-500/20 border-amber-400';
      case 'SES':
        return 'text-cyan-400 bg-cyan-500/20 border-cyan-400';
      default:
        return 'text-white bg-white/20 border-white';
    }
  };
  // Color schemes per view mode
  const getTempColor = (temp: number) => {
    if (temp < 22) return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (temp < 24) return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (temp < 26) return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };
  const getEnergyColor = (kwh: number) => {
    if (kwh < 4) return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (kwh < 8) return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (kwh < 12) return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };
  const getComfortColor = (score: number) => {
    if (score >= 85) return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (score >= 70) return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (score >= 50) return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };
  const getRoomColors = (room: Room) => {
    switch (viewMode) {
      case 'energy':
        return getEnergyColor(ROOM_ENERGY[room.id] || 0);
      case 'comfort':
        return getComfortColor(ROOM_COMFORT[room.id] || 0);
      default:
        return getTempColor(room.temp);
    }
  };
  const getRoomDisplayValue = (room: Room) => {
    switch (viewMode) {
      case 'energy':
        return `${ROOM_ENERGY[room.id] || 0} kWh`;
      case 'comfort':
        return `${ROOM_COMFORT[room.id] || 0}점`;
      default:
        return `${room.temp}°C`;
    }
  };
  const getLegendItems = () => {
    switch (viewMode) {
      case 'energy':
        return [{
          color: 'bg-emerald-500/60',
          label: '절약'
        }, {
          color: 'bg-cyan-500/60',
          label: '보통'
        }, {
          color: 'bg-amber-500/60',
          label: '높음'
        }, {
          color: 'bg-red-500/60',
          label: '과다'
        }];
      case 'comfort':
        return [{
          color: 'bg-emerald-500/60',
          label: '쾌적'
        }, {
          color: 'bg-cyan-500/60',
          label: '양호'
        }, {
          color: 'bg-amber-500/60',
          label: '보통'
        }, {
          color: 'bg-red-500/60',
          label: '불쾌'
        }];
      default:
        return [{
          color: 'bg-cyan-500/60',
          label: '낮음'
        }, {
          color: 'bg-emerald-500/60',
          label: '적정'
        }, {
          color: 'bg-amber-500/60',
          label: '높음'
        }];
    }
  };
  const getLegendTitle = () => {
    switch (viewMode) {
      case 'energy':
        return '에너지';
      case 'comfort':
        return '쾌적성';
      default:
        return '실내온도';
    }
  };
  const getZoomTransform = () => {
    if (!isZoomed || !selectedRoomId) return {
      x: 0,
      y: 0,
      scale: 1
    };
    const coords = ROOM_COORDINATES[selectedRoomId] || DEFAULT_COORDS;
    const top = parsePercent(coords.top);
    const left = parsePercent(coords.left);
    const width = parsePercent(coords.width);
    const height = parsePercent(coords.height);
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const scale = 2.2;
    const x = (50 - centerX) * scale;
    const y = (50 - centerY) * scale;
    return {
      x: `${x}%`,
      y: `${y}%`,
      scale
    };
  };
  const zoomState = getZoomTransform();
  return <div className="relative w-full flex items-center justify-center p-2 overflow-hidden h-full" data-id="element-78">
      {/* Zoom Out Button Overlay */}
      <AnimatePresence data-id="element-79">
        {isZoomed && onZoomOut && <motion.button initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} onClick={e => {
        e.stopPropagation();
        onZoomOut();
      }} className="absolute top-4 left-4 z-50 p-2 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-white hover:bg-black/80 transition-colors shadow-lg group" data-id="element-80">
            <div className="flex items-center gap-2 px-2" data-id="element-81">
              <ZoomOutIcon className="w-4 h-4" data-id="element-82" />
              <span className="text-xs font-medium pr-1" data-id="element-83">돌아가기</span>
            </div>
          </motion.button>}
      </AnimatePresence>

      {/* Key Map / Minimap */}
      <AnimatePresence data-id="element-84">
        {isZoomed && imageLoaded && selectedRoomId && <motion.div initial={{
        opacity: 0,
        scale: 0.8,
        y: -10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.8,
        y: -10
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} className="absolute top-4 right-4 z-50" data-id="element-85">
            <div className="bg-black/70 backdrop-blur-md border border-white/15 rounded-xl p-2 shadow-2xl" data-id="element-86">
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-1.5 px-0.5" data-id="element-87">
                Key Map
              </div>
              <div className="relative w-[120px] overflow-hidden rounded-lg border border-white/10" data-id="element-88">
                <img src={FLOOR_PLAN_URL} alt="Floor Plan Overview" className="w-full h-auto block opacity-60" crossOrigin="anonymous" data-id="element-89" />
                {rooms.map(room => {
              const coords = ROOM_COORDINATES[room.id] || DEFAULT_COORDS;
              const isSelected = selectedRoomId === room.id;
              return <div key={room.id} className={`absolute transition-all duration-500 rounded-sm ${isSelected ? 'bg-cyan-400/40 border-2 border-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]' : 'bg-white/5 border border-white/10'}`} style={{
                top: coords.top,
                left: coords.left,
                width: coords.width,
                height: coords.height
              }} data-id="element-90" />;
            })}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      <motion.div className="relative w-full transition-transform duration-700 ease-in-out" style={{
      maxWidth: 580,
      transformOrigin: 'center center'
    }} animate={{
      scale: zoomState.scale,
      x: zoomState.x,
      y: zoomState.y
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 25
    }} data-id="element-91">
        <img src={FLOOR_PLAN_URL} alt="Floor Plan" onLoad={() => setImageLoaded(true)} onError={() => setImageError(true)} className={`w-full h-auto block opacity-80 drop-shadow-2xl transition-opacity duration-500 ${imageLoaded ? 'opacity-80' : 'opacity-0'}`} crossOrigin="anonymous" data-id="element-92" />

        {!imageLoaded && !imageError && <div className="w-full flex items-center justify-center py-32" data-id="element-93">
            <div className="flex flex-col items-center gap-3 text-slate-500" data-id="element-94">
              <div className="w-8 h-8 border-2 border-cyan-400/50 border-t-cyan-400 rounded-full animate-spin" data-id="element-95" />
              <span className="text-xs font-mono uppercase tracking-wider" data-id="element-96">
                평면도 로딩 중...
              </span>
            </div>
          </div>}

        {imageError && <div className="w-full flex items-center justify-center py-32" data-id="element-97">
            <div className="flex flex-col items-center gap-3 text-slate-500" data-id="element-98">
              <ImageOffIcon className="w-10 h-10 text-slate-600" data-id="element-99" />
              <span className="text-xs font-mono uppercase tracking-wider" data-id="element-100">
                평면도가 없습니다.
              </span>
            </div>
          </div>}

        {imageLoaded && <div className="absolute inset-0" data-id="element-101">
            {rooms.map(room => {
          const coords = ROOM_COORDINATES[room.id] || DEFAULT_COORDS;
          const isSelected = selectedRoomId === room.id;
          const colors = getRoomColors(room);
          const isDimmed = isZoomed && !isSelected;
          return <motion.button key={`${room.id}-${viewMode}`} onClick={e => {
            if (!isZoomed) onSelectRoom(room.id);
          }} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: isDimmed ? 0.3 : 1,
            scale: isSelected ? 1.03 : 1,
            zIndex: isSelected ? 20 : 10
          }} whileHover={{
            scale: isDimmed ? 1 : 1.02,
            zIndex: 15,
            opacity: isDimmed ? 0.8 : 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} style={{
            top: coords.top,
            left: coords.left,
            width: coords.width,
            height: coords.height
          }} className={`
                    absolute rounded-2xl border-2 backdrop-blur-[2px] flex flex-col items-center justify-center group cursor-pointer
                    ${colors.bg}
                    ${isSelected ? 'border-white shadow-[0_0_20px_rgba(255,255,255,0.3)] ring-2 ring-cyan-400/50' : `${colors.border}`}
                    ${isZoomed && isSelected ? '!border-transparent !bg-transparent !shadow-none !backdrop-blur-none' : ''}
                  `} data-id="element-102">
                  {(!isZoomed || !isSelected) && <>
                      {isSelected && <span className="absolute inset-0 rounded-2xl animate-pulse bg-white/10" data-id="element-103" />}
                      <div className={`relative z-10 flex flex-col items-center text-center p-1.5 md:p-2 rounded-lg transition-all duration-300 ${isSelected ? 'bg-black/50 backdrop-blur-md' : 'bg-black/30'}`} data-id="element-104">
                        <span className={`text-[8px] md:text-[10px] font-bold uppercase tracking-wider mb-0.5 ${isSelected ? 'text-white' : 'text-white/80'}`} data-id="element-105">
                          {room.name}
                        </span>
                        <span className={`text-sm md:text-lg font-mono font-bold ${colors.text} drop-shadow-md`} data-id="element-106">
                          {getRoomDisplayValue(room)}
                        </span>
                      </div>
                      <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${isSelected ? 'bg-white shadow-[0_0_8px_white]' : colors.text.replace('text-', 'bg-')}`} data-id="element-107" />
                    </>}

                  {isZoomed && isSelected && MOCK_SENSORS[room.id]?.map(sensor => {
              const Icon = getSensorIcon(sensor.type);
              const colorClasses = getSensorColor(sensor.type);
              const isSensorSelected = selectedSensorId === sensor.id;
              return <motion.div key={sensor.id} initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 0.3,
                type: 'spring'
              }} className="absolute" style={{
                left: `${sensor.x}%`,
                top: `${sensor.y}%`,
                transform: 'translate(-50%, -50%)'
              }} onClick={e => {
                e.stopPropagation();
                onSelectSensor?.(sensor);
              }} data-id="element-108">
                          {sensor.active && <span className={`absolute inset-0 rounded-full animate-ping opacity-75 ${colorClasses.split(' ')[2].replace('border-', 'bg-')}`} data-id="element-109" />}
                          <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center border-2 shadow-lg transition-all duration-300 ${colorClasses} ${isSensorSelected ? 'scale-125 ring-2 ring-white shadow-[0_0_15px_rgba(255,255,255,0.5)]' : 'hover:scale-110'}`} data-id="element-110">
                            <Icon className="w-3 h-3" data-id="element-111" />
                          </div>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded text-[8px] font-mono text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-112">
                            {sensor.label}
                          </div>
                        </motion.div>;
            })}
                </motion.button>;
        })}
          </div>}

        {/* Legend */}
        {imageLoaded && !isZoomed && <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-md px-3 py-2 rounded-lg border border-white/10 text-[10px] text-slate-400 hidden sm:flex items-center gap-3" data-id="element-113">
            <MapPinIcon className="w-3 h-3 text-cyan-400 flex-shrink-0" data-id="element-114" />
            <span className="uppercase tracking-wider font-bold" data-id="element-115">
              {getLegendTitle()}
            </span>
            <span className="text-slate-500" data-id="element-116">|</span>
            <div className="flex items-center gap-2" data-id="element-117">
              {getLegendItems().map(item => <span key={item.label} className="flex items-center gap-1" data-id="element-118">
                  <span className={`w-2 h-2 rounded-full ${item.color}`} data-id="element-119" />
                  {item.label}
                </span>)}
            </div>
          </div>}
      </motion.div>
    </div>;
}